"use client";

import type { FC } from "react";
import { Card, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@akashnetwork/ui/components";
import { cn } from "@akashnetwork/ui/utils";
import { useProjectAccess } from "@src/hooks/useProjectAccess/useProjectAccess";
import type { DeploymentsViewMode } from "@src/store/deploymentsViewStore";
import type { ListedDeploymentDto } from "@src/types/deployment";
import type { ApiProviderList } from "@src/types/provider";
import { CREATING_STATUS, StatusBadge } from "../DeploymentDetail/DeploymentStatusBadge";
import { DeploymentCard } from "./DeploymentCard";
import { DeploymentRow } from "./DeploymentRow";
import type { CreatingDeployment } from "./useCreatingDeployments";
export const DEPENDENCIES = {
  useProjectAccess,
  DeploymentCard,
  DeploymentRow
};
const LOADING_PLACEHOLDERS = ["first", "second", "third"];
const UNNAMED_CREATING_DEPLOYMENT = "New deployment";

/**
 * The archive renders a second table below this one, and only a fixed layout keeps the two reading as one grid:
 * an auto layout sizes each table to its own rows, which differ by the archive's missing checkbox and its much
 * shorter endpoint text. Under a fixed layout only the header row's widths count, so the columns are declared
 * there and the body cells carry none.
 */
const TABLE_LAYOUT = "table-fixed";

/** The project page's deployments column narrows to 55.5rem beside its aside on a 1280px screen, so this must stay under that. */
const MIN_TABLE_WIDTH = "min-w-[52rem]";

/** Wide enough for the hardware summary's own fixed columns, and for the checkbox the archive's rows leave out. */
const HARDWARE_COLUMN = "w-[14rem]";
const CONTROLS_COLUMN = "w-[6.25rem]";
export interface DeploymentsCollectionProps {
  deployments: ListedDeploymentDto[];
  /** Shown on top and not opened, since their deployments are not on chain for their pages to read yet. */
  creatingDeployments?: CreatingDeployment[];
  providers: ApiProviderList[] | undefined;
  viewMode: DeploymentsViewMode;
  isLoading?: boolean;
  isSelectable?: boolean;
  selectedIds?: string[];
  closingDseqs?: ReadonlySet<string>;
  onSelect?: (input: {
    id: string;
    isShiftPressed: boolean;
  }) => void;
  onDeploymentClosed?: () => void;
  dependencies?: typeof DEPENDENCIES;
}
export const DeploymentsCollection: FC<DeploymentsCollectionProps> = ({
  deployments,
  creatingDeployments = [],
  providers,
  viewMode,
  isLoading,
  isSelectable,
  selectedIds,
  closingDseqs,
  onSelect,
  onDeploymentClosed,
  dependencies: d = DEPENDENCIES
}) => {
  const {
    canManageDeployments
  } = d.useProjectAccess();
  const itemProps = (deployment: ListedDeploymentDto) => {
    const canManage = canManageDeployments(deployment.projectId);
    return {
      deployment,
      providers,
      isSelectable: isSelectable && canManage,
      canManage,
      isSelected: selectedIds?.includes(deployment.dseq) ?? false,
      isClosing: closingDseqs?.has(deployment.dseq) ?? false,
      onSelect,
      onDeploymentClosed
    };
  };
  if (viewMode === "grid") {
    return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {creatingDeployments.map(deployment => <CreatingCard key={deployment.dseq} deployment={deployment} />)}
        {isLoading ? LOADING_PLACEHOLDERS.map(key => <CardPlaceholder key={key} />) : deployments.map(deployment => <d.DeploymentCard key={deployment.dseq} {...itemProps(deployment)} />)}
      </div>;
  }
  return <div className="overflow-x-auto rounded-md border bg-card" data-sentry-component="DeploymentsCollection" data-sentry-source-file="DeploymentsCollection.tsx">
      <Table className={cn(MIN_TABLE_WIDTH, TABLE_LAYOUT)} data-sentry-element="Table" data-sentry-source-file="DeploymentsCollection.tsx">
        <TableHeader data-sentry-element="TableHeader" data-sentry-source-file="DeploymentsCollection.tsx">
          <TableRow data-sentry-element="TableRow" data-sentry-source-file="DeploymentsCollection.tsx">
            <TableHead className="w-52" data-sentry-element="TableHead" data-sentry-source-file="DeploymentsCollection.tsx">Status</TableHead>
            <TableHead data-sentry-element="TableHead" data-sentry-source-file="DeploymentsCollection.tsx">Deployment</TableHead>
            <TableHead data-sentry-element="TableHead" data-sentry-source-file="DeploymentsCollection.tsx">Endpoint</TableHead>
            <TableHead className={HARDWARE_COLUMN} data-sentry-element="TableHead" data-sentry-source-file="DeploymentsCollection.tsx">Hardware</TableHead>
            <TableHead className={CONTROLS_COLUMN} data-sentry-element="TableHead" data-sentry-source-file="DeploymentsCollection.tsx" />
          </TableRow>
        </TableHeader>
        <TableBody data-sentry-element="TableBody" data-sentry-source-file="DeploymentsCollection.tsx">
          {creatingDeployments.map(deployment => <CreatingRow key={deployment.dseq} deployment={deployment} />)}
          {isLoading ? LOADING_PLACEHOLDERS.map(key => <RowPlaceholder key={key} />) : deployments.map(deployment => <d.DeploymentRow key={deployment.dseq} {...itemProps(deployment)} />)}
        </TableBody>
      </Table>
    </div>;
};
const CreatingCard: FC<{
  deployment: CreatingDeployment;
}> = ({
  deployment
}) => <Card className="flex flex-col overflow-hidden" data-sentry-element="Card" data-sentry-component="CreatingCard" data-sentry-source-file="DeploymentsCollection.tsx">
    <div className="flex items-start justify-between gap-3 p-5">
      <p className="min-w-0 truncate font-semibold">{deployment.name || UNNAMED_CREATING_DEPLOYMENT}</p>
      <StatusBadge label={CREATING_STATUS.label} tone={CREATING_STATUS.tone} className="shrink-0" data-sentry-element="StatusBadge" data-sentry-source-file="DeploymentsCollection.tsx" />
    </div>
  </Card>;
const CreatingRow: FC<{
  deployment: CreatingDeployment;
}> = ({
  deployment
}) => <TableRow data-sentry-element="TableRow" data-sentry-component="CreatingRow" data-sentry-source-file="DeploymentsCollection.tsx">
    <TableCell data-sentry-element="TableCell" data-sentry-source-file="DeploymentsCollection.tsx">
      <StatusBadge label={CREATING_STATUS.label} tone={CREATING_STATUS.tone} data-sentry-element="StatusBadge" data-sentry-source-file="DeploymentsCollection.tsx" />
    </TableCell>
    <TableCell data-sentry-element="TableCell" data-sentry-source-file="DeploymentsCollection.tsx">
      <p className="truncate font-semibold">{deployment.name || UNNAMED_CREATING_DEPLOYMENT}</p>
    </TableCell>
    <TableCell colSpan={3} data-sentry-element="TableCell" data-sentry-source-file="DeploymentsCollection.tsx" />
  </TableRow>;
const CardPlaceholder: FC = () => <Card className="flex flex-col overflow-hidden" data-testid="deployment-placeholder" data-sentry-element="Card" data-sentry-component="CardPlaceholder" data-sentry-source-file="DeploymentsCollection.tsx">
    <div className="flex flex-1 flex-col gap-3 p-5">
      <div className="flex items-start justify-between gap-3">
        <Skeleton className="h-5 w-32" data-sentry-element="Skeleton" data-sentry-source-file="DeploymentsCollection.tsx" />
        <Skeleton className="h-6 w-20 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="DeploymentsCollection.tsx" />
      </div>
      <Skeleton className="h-4 w-48" data-sentry-element="Skeleton" data-sentry-source-file="DeploymentsCollection.tsx" />
    </div>
    <div className="flex items-center justify-between gap-3 border-t px-5 py-2">
      <Skeleton className="h-4 w-40" data-sentry-element="Skeleton" data-sentry-source-file="DeploymentsCollection.tsx" />
      <Skeleton className="h-10 w-10 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="DeploymentsCollection.tsx" />
    </div>
  </Card>;
const RowPlaceholder: FC = () => <TableRow data-testid="deployment-placeholder" data-sentry-element="TableRow" data-sentry-component="RowPlaceholder" data-sentry-source-file="DeploymentsCollection.tsx">
    <TableCell data-sentry-element="TableCell" data-sentry-source-file="DeploymentsCollection.tsx">
      <Skeleton className="h-6 w-20 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="DeploymentsCollection.tsx" />
    </TableCell>
    <TableCell data-sentry-element="TableCell" data-sentry-source-file="DeploymentsCollection.tsx">
      <Skeleton className="h-4 w-32" data-sentry-element="Skeleton" data-sentry-source-file="DeploymentsCollection.tsx" />
    </TableCell>
    <TableCell data-sentry-element="TableCell" data-sentry-source-file="DeploymentsCollection.tsx">
      <Skeleton className="h-4 w-40" data-sentry-element="Skeleton" data-sentry-source-file="DeploymentsCollection.tsx" />
    </TableCell>
    <TableCell data-sentry-element="TableCell" data-sentry-source-file="DeploymentsCollection.tsx">
      <Skeleton className="h-4 w-36" data-sentry-element="Skeleton" data-sentry-source-file="DeploymentsCollection.tsx" />
    </TableCell>
    <TableCell className="pl-0" data-sentry-element="TableCell" data-sentry-source-file="DeploymentsCollection.tsx">
      <Skeleton className="ml-auto h-10 w-10 rounded-full" data-sentry-element="Skeleton" data-sentry-source-file="DeploymentsCollection.tsx" />
    </TableCell>
  </TableRow>;